import type { TaApprovalDecision, TaApprovalOutcome } from './taProposalTypes';

export type TaApprovalSubmitter = (
  decision: TaApprovalDecision,
  signal: AbortSignal,
) => Promise<TaApprovalOutcome>;

const NO_SUBMITTER =
  'No approval channel is registered in this build, so nothing can be applied from here.';

const ALREADY_SUBMITTED =
  'That proposal has already been submitted once. A second submit would risk a second write.';

const OTHER_CONVERSATION =
  'This proposal belongs to a different conversation than the one open here.';

const NOT_DELIVERED =
  'This proposal arrived in an earlier session, so whether it was already applied is not known here. Ask in the chat before acting on it again.';

let submitter: TaApprovalSubmitter | null = null;
let submitterConversationId: string | null = null;

const inFlight = new Set<string>();
const settled = new Set<string>();
const delivered = new Map<string, string>();

export const markTaProposalDelivered = (proposalId: string, conversationId: string): void => {
  delivered.set(proposalId, conversationId);
};

export const forgetTaProposalDelivery = (proposalId: string): void => {
  delivered.delete(proposalId);
};

export const taProposalWasDelivered = (proposalId: string): boolean =>
  submitterConversationId !== null && delivered.get(proposalId) === submitterConversationId;

export const registerTaApprovalSubmitter = (
  next: TaApprovalSubmitter | null,
  conversationId: string | null = null,
): void => {
  submitter = next;
  submitterConversationId = next ? conversationId : null;
};

export const hasTaApprovalSubmitter = (): boolean => submitter !== null;

export const taProposalIsSettled = (proposalId: string): boolean =>
  settled.has(proposalId) || inFlight.has(proposalId);

export const submitTaApproval = async (
  decision: TaApprovalDecision,
  signal: AbortSignal,
): Promise<TaApprovalOutcome> => {
  if (!decision.approvalRequestId) {
    return { ok: false, reason: 'This proposal carries no server approval identity.' };
  }
  const active = submitter;
  if (!active) {
    return { ok: false, reason: NO_SUBMITTER };
  }
  const deliveredTo = delivered.get(decision.proposalId);
  if (deliveredTo === undefined) {
    return { ok: false, reason: NOT_DELIVERED };
  }
  if (deliveredTo !== submitterConversationId) {
    return { ok: false, reason: OTHER_CONVERSATION };
  }
  if (settled.has(decision.proposalId) || inFlight.has(decision.proposalId)) {
    return { ok: false, reason: ALREADY_SUBMITTED };
  }

  inFlight.add(decision.proposalId);
  settled.add(decision.proposalId);
  try {
    return await active(decision, signal);
  } catch (error) {
    console.warn('[central-chat] approval submit returned no outcome', error);
    return { ok: true, pending: true };
  } finally {
    inFlight.delete(decision.proposalId);
  }
};

export const resetTaApprovalLedgerForTesting = (): void => {
  inFlight.clear();
  settled.clear();
  delivered.clear();
};
